import { useState, useEffect, useMemo, useCallback } from 'react';
import { VisualizationFilters } from '../components/pitch/types';
import { StatType } from '../types/database';

// Type definitions
export type VisualizationTypeOption = 'scatter' | 'trajectory' | 'heatmap' | 'grid' | 'team_shape';
export type VisualizationType = VisualizationTypeOption[];
export type PointSelectionOption = 'source' | 'destination';
export type PointSelection = PointSelectionOption[];
export type TrajectoryDensity = 'all' | 'medium' | 'low' | 'first_last_only';
export type PerStatVisualizationType = VisualizationTypeOption[];
export type ScatterShape = 'circle' | 'square' | 'triangle' | 'star';
export type TrajectoryStyle = 'solid-arrow' | 'dotted-arrow' | 'progressive';

export interface UseMapVisualizationOptions {
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName?: string;
  awayTeamName?: string;
  statTypes: StatType[];
  availableStatTypeIds: Set<string>;
  availableTags: string[];
  players: Array<{ player_id: string; full_name: string; kit_number: number | null; team_id: string; team_name: string }>;
  events: any[]; // VisualizationEvent[]
}

export interface UseMapVisualizationReturn {
  // State
  visualizationType: VisualizationType;
  setVisualizationType: (type: VisualizationType) => void;
  pointSelection: PointSelection;
  setPointSelection: (selection: PointSelection) => void;
  trajectoryDensity: TrajectoryDensity;
  setTrajectoryDensity: (density: TrajectoryDensity) => void;
  filters: VisualizationFilters;
  setFilters: (filters: VisualizationFilters | ((prev: VisualizationFilters) => VisualizationFilters)) => void;
  colorBy: 'team' | 'stat_type';
  setColorBy: (colorBy: 'team' | 'stat_type') => void;
  statTypeVisualizationMap: Record<string, PerStatVisualizationType>;
  setStatTypeVisualizationMap: (map: Record<string, PerStatVisualizationType> | ((prev: Record<string, PerStatVisualizationType>) => Record<string, PerStatVisualizationType>)) => void;
  normalizeToFirstHalf: boolean;
  setNormalizeToFirstHalf: (normalize: boolean) => void;
  compareMode: boolean;
  setCompareMode: (compare: boolean) => void;
  homeTeamAttackingDirection: 'left' | 'right';
  setHomeTeamAttackingDirection: (direction: 'left' | 'right') => void;
  awayTeamAttackingDirection: 'left' | 'right';
  setAwayTeamAttackingDirection: (direction: 'left' | 'right') => void;
  whiteBackground: boolean;
  setWhiteBackground: (white: boolean) => void;
  heatmapRadius: number;
  setHeatmapRadius: (radius: number) => void;
  heatmapSmoothness: number;
  setHeatmapSmoothness: (smoothness: number) => void;
  heatmapOpacity: number;
  setHeatmapOpacity: (opacity: number) => void;
  gridSize: number;
  setGridSize: (size: number) => void;
  gridShowLabels: boolean;
  setGridShowLabels: (show: boolean) => void;
  gridOpacity: number;
  setGridOpacity: (opacity: number) => void;
  scatterShapes: Record<string, ScatterShape>;
  setScatterShapes: (shapes: Record<string, ScatterShape> | ((prev: Record<string, ScatterShape>) => Record<string, ScatterShape>)) => void;
  trajectoryStyles: Record<string, TrajectoryStyle>;
  setTrajectoryStyles: (styles: Record<string, TrajectoryStyle> | ((prev: Record<string, TrajectoryStyle>) => Record<string, TrajectoryStyle>)) => void;
  teamColors: Record<string, string>;
  setTeamColors: (colors: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  statTypeColors: Record<string, string>;
  setStatTypeColors: (colors: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  selectedStatTypes: Array<{ id: string; name: string }>;
  setSelectedStatTypes: (types: Array<{ id: string; name: string }>) => void;
  selectedTags: Array<{ id: string; name: string }>;
  setSelectedTags: (tags: Array<{ id: string; name: string }>) => void;
  
  // Computed values
  statTypeOptions: Array<{ id: string; name: string }>;
  playerOptions: Array<{ id: string; name: string }>;
  tagOptions: Array<{ id: string; name: string }>;
  teamOptions: Array<{ id: string; name: string }>;
  hasActiveFilters: boolean;
  hasFilteredData: boolean;
  shapesKey: string;
  stylesKey: string;
  
  // Helper functions
  handleFilterChange: (key: keyof VisualizationFilters, value: any) => void;
  handleClearFilters: () => void;
  getStatTypeColor: (statTypeId: string, teamId?: string) => string;
  getVisualizationTypeForItem: (itemId: string, isStatType: boolean) => 'scatter' | 'trajectory' | 'heatmap' | 'grid' | 'both';
}

/**
 * Custom hook for managing map visualization state and logic
 * Extracted from MapsTab to be reusable across components
 */
export const useMapVisualization = (
  options: UseMapVisualizationOptions
): UseMapVisualizationReturn => {
  const {
    homeTeamId,
    awayTeamId,
    homeTeamName = 'Home Team',
    awayTeamName = 'Away Team',
    statTypes,
    availableStatTypeIds,
    availableTags,
    players,
    events,
  } = options;

  // Visualization type state
  const [visualizationType, setVisualizationType] = useState<VisualizationType>(['scatter']);
  const [pointSelection, setPointSelection] = useState<PointSelection>(['source', 'destination']);
  const [trajectoryDensity, setTrajectoryDensity] = useState<TrajectoryDensity>('all');
  const [filters, setFilters] = useState<VisualizationFilters>({
    team_id: [homeTeamId, awayTeamId] // Default to both teams selected
  });
  const [colorBy, setColorBy] = useState<'team' | 'stat_type'>('team');
  
  // Per-stat-type visualization mapping
  const [statTypeVisualizationMap, setStatTypeVisualizationMap] = useState<Record<string, PerStatVisualizationType>>({});
  
  // View settings
  const [normalizeToFirstHalf, setNormalizeToFirstHalf] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [homeTeamAttackingDirection, setHomeTeamAttackingDirection] = useState<'left' | 'right'>('right');
  const [awayTeamAttackingDirection, setAwayTeamAttackingDirection] = useState<'left' | 'right'>('left');
  const [whiteBackground, setWhiteBackground] = useState(false);
  
  // Heatmap controls
  const [heatmapRadius, setHeatmapRadius] = useState(100);
  const [heatmapSmoothness, setHeatmapSmoothness] = useState(3.0);
  const [heatmapOpacity, setHeatmapOpacity] = useState(0.6);
  
  // Grid controls
  const [gridSize, setGridSize] = useState(10);
  const [gridShowLabels, setGridShowLabels] = useState(true);
  const [gridOpacity, setGridOpacity] = useState(0.6);
  
  // Plot type customization
  const [scatterShapes, setScatterShapes] = useState<Record<string, ScatterShape>>({});
  const [trajectoryStyles, setTrajectoryStyles] = useState<Record<string, TrajectoryStyle>>({});
  
  // Color state
  const [teamColors, setTeamColors] = useState<Record<string, string>>({
    [homeTeamId]: '#3B82F6', // Blue for home team
    [awayTeamId]: '#EF4444', // Red for away team
  });
  const [statTypeColors, setStatTypeColors] = useState<Record<string, string>>({});
  
  // Selected options for chip display
  const [selectedStatTypes, setSelectedStatTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedTags, setSelectedTags] = useState<Array<{ id: string; name: string }>>([]);

  // Create hash keys for shapes/styles to use in component keys
  const shapesKey = useMemo(() => {
    const entries = Object.entries(scatterShapes).sort().map(([k, v]) => `${k}:${v}`).join(',');
    return `${entries || 'default'}-norm-${normalizeToFirstHalf}`;
  }, [scatterShapes, normalizeToFirstHalf]);
  
  const stylesKey = useMemo(() => {
    const entries = Object.entries(trajectoryStyles).sort().map(([k, v]) => `${k}:${v}`).join(',');
    return `${entries || 'default'}-norm-${normalizeToFirstHalf}`;
  }, [trajectoryStyles, normalizeToFirstHalf]);

  // Computed options
  const statTypeOptions = useMemo(() => {
    return statTypes
      .filter((stat) => availableStatTypeIds.has(stat.id))
      .map((stat) => ({
        id: stat.id,
        name: stat.display_name || stat.short_description || stat.stat_key,
      }));
  }, [statTypes, availableStatTypeIds]);

  const playerOptions = useMemo(() => [
    { id: '', name: 'All Players' },
    ...players.map((player) => ({
      id: player.player_id,
      name: `${player.full_name}${player.kit_number ? ` #${player.kit_number}` : ''} (${player.team_name})`,
    })),
  ], [players]);

  const tagOptions = useMemo(() => availableTags.map((tag) => ({
    id: tag,
    name: tag.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
  })), [availableTags]);

  const teamOptions = useMemo(() => [
    { id: homeTeamId, name: homeTeamName },
    { id: awayTeamId, name: awayTeamName },
  ], [homeTeamId, awayTeamId, homeTeamName, awayTeamName]);

  // Filter handlers
  const handleFilterChange = useCallback((key: keyof VisualizationFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      team_id: [homeTeamId, awayTeamId] // Reset to both teams selected
    });
    setStatTypeVisualizationMap({});
  }, [homeTeamId, awayTeamId]);

  // Initialize visualization map with default 'scatter' for selected stat types
  useEffect(() => {
    if (filters.stat_type_id && filters.stat_type_id.length > 0) {
      setStatTypeVisualizationMap((prev) => {
        const newMap: Record<string, PerStatVisualizationType> = {};
        filters.stat_type_id?.forEach((id) => {
          // Use existing value if available, otherwise default to 'scatter'
          newMap[id] = prev[id] || ['scatter'];
        });
        // Only update if the map actually changed to prevent unnecessary re-renders
        const prevKeys = Object.keys(prev).sort().join(',');
        const newKeys = Object.keys(newMap).sort().join(',');
        if (prevKeys !== newKeys) {
          return newMap;
        }
        // Check if values changed for existing keys
        const hasValueChange = filters.stat_type_id?.some((id) => prev[id] !== newMap[id]);
        if (hasValueChange) {
          return newMap;
        }
        return prev; // No change needed, return previous value to prevent re-render
      });
    } else {
      // Clear all mappings if no stat types are selected
      // Only update if map is not already empty to prevent infinite loops
      setStatTypeVisualizationMap((prev) => {
        if (Object.keys(prev).length === 0) {
          return prev; // Already empty, no change needed
        }
        return {}; // Not empty, set to empty
      });
    }
  }, [filters.stat_type_id]);

  // Computed values
  const hasActiveFilters = useMemo(() => {
    return Boolean(
      (filters.stat_type_id && filters.stat_type_id.length > 0) ||
      (filters.team_id && filters.team_id.length > 0) ||
      filters.player_id ||
      (filters.tags && filters.tags.length > 0) ||
      filters.match_period
    );
  }, [filters]);

  const hasFilteredData = useMemo(() => events && events.length > 0, [events]);

  // Generate color for stat type (with optional teamId for team-specific colors)
  const getStatTypeColor = useCallback((statTypeId: string, teamId?: string): string => {
    // Try team-specific color first if teamId is provided (user customization)
    if (teamId) {
      const teamSpecificKey = `${statTypeId}-${teamId}`;
      if (statTypeColors[teamSpecificKey]) {
        return statTypeColors[teamSpecificKey];
      }
    }
    // Fall back to stat-type-only color (for backward compatibility and default shared color)
    if (statTypeColors[statTypeId]) {
      return statTypeColors[statTypeId];
    }
    // Generate hash from statTypeId ONLY (not including teamId) to get same color for same stat type across teams
    // Users can still customize per-team by setting team-specific colors
    const hash = statTypeId.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    const hue = Math.abs(hash) % 360;
    const h = hue / 360;
    const s = 0.7;
    const l = 0.5;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h * 6) % 2 - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if (h < 1/6) { r = c; g = x; b = 0; }
    else if (h < 2/6) { r = x; g = c; b = 0; }
    else if (h < 3/6) { r = 0; g = c; b = x; }
    else if (h < 4/6) { r = 0; g = x; b = c; }
    else if (h < 5/6) { r = x; g = 0; b = c; }
    else { r = c; g = 0; b = x; }
    const toHex = (n: number) => {
      const hex = Math.round((n + m) * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }, [statTypeColors]);

  // Determine visualization type for each stat type/team
  // Note: This function excludes 'team_shape' as it's handled separately in the renderer
  const getVisualizationTypeForItem = useCallback((itemId: string, isStatType: boolean): 'scatter' | 'trajectory' | 'heatmap' | 'grid' | 'both' => {
    // Helper to filter out 'team_shape' from visualization types
    const filterTeamShape = (types: VisualizationTypeOption[]): Array<'scatter' | 'trajectory' | 'heatmap' | 'grid'> => {
      return types.filter((t): t is 'scatter' | 'trajectory' | 'heatmap' | 'grid' => 
        t !== 'team_shape'
      );
    };

    if (isStatType) {
      if (filters.stat_type_id && filters.stat_type_id.length > 0 && Object.keys(statTypeVisualizationMap).length > 0) {
        const mappedTypes = filterTeamShape(statTypeVisualizationMap[itemId] || ['scatter']);
        // Check if both scatter and trajectory are present (these are the only types with pickers)
        const hasScatter = mappedTypes.includes('scatter');
        const hasTrajectory = mappedTypes.includes('trajectory');
        if (hasScatter && hasTrajectory) return 'both';
        // If only one of them, return that one
        if (hasScatter) return 'scatter';
        if (hasTrajectory) return 'trajectory';
        // Fallback to first type
        return mappedTypes[0] || 'scatter';
      } else {
        // Check global visualization type setting
        const filteredTypes = filterTeamShape(visualizationType);
        const hasScatter = filteredTypes.includes('scatter');
        const hasTrajectory = filteredTypes.includes('trajectory');
        if (hasScatter && hasTrajectory) return 'both';
        // If only one of them, return that one
        if (hasScatter) return 'scatter';
        if (hasTrajectory) return 'trajectory';
        // Fallback to first type
        return filteredTypes[0] || 'scatter';
      }
    } else {
      // For teams, check if per-stat-type mapping is active
      if (filters.stat_type_id && filters.stat_type_id.length > 0 && Object.keys(statTypeVisualizationMap).length > 0) {
        const teamEvents = events.filter((e: any) => e.team_id === itemId);
        const allTypes = new Set<VisualizationTypeOption>();
        teamEvents.forEach((e: any) => {
          const vizTypes = statTypeVisualizationMap[e.stat_type_id] || ['scatter'];
          vizTypes.forEach(t => allTypes.add(t));
        });
        const types = filterTeamShape(Array.from(allTypes));
        // Check if both scatter and trajectory are present (these are the only types with pickers)
        const hasScatter = types.includes('scatter');
        const hasTrajectory = types.includes('trajectory');
        if (hasScatter && hasTrajectory) return 'both';
        // If only one of them, return that one
        if (hasScatter) return 'scatter';
        if (hasTrajectory) return 'trajectory';
        // Fallback to first type or scatter
        return types[0] || 'scatter';
      } else {
        // Use global visualization type setting for teams
        const filteredTypes = filterTeamShape(visualizationType);
        const hasScatter = filteredTypes.includes('scatter');
        const hasTrajectory = filteredTypes.includes('trajectory');
        if (hasScatter && hasTrajectory) return 'both';
        // If only one of them, return that one
        if (hasScatter) return 'scatter';
        if (hasTrajectory) return 'trajectory';
        // Fallback to first type or scatter
        return filteredTypes[0] || 'scatter';
      }
    }
  }, [filters.stat_type_id, statTypeVisualizationMap, visualizationType, events]);

  return {
    // State
    visualizationType,
    setVisualizationType,
    pointSelection,
    setPointSelection,
    trajectoryDensity,
    setTrajectoryDensity,
    filters,
    setFilters,
    colorBy,
    setColorBy,
    statTypeVisualizationMap,
    setStatTypeVisualizationMap,
    normalizeToFirstHalf,
    setNormalizeToFirstHalf,
    compareMode,
    setCompareMode,
    homeTeamAttackingDirection,
    setHomeTeamAttackingDirection,
    awayTeamAttackingDirection,
    setAwayTeamAttackingDirection,
    whiteBackground,
    setWhiteBackground,
    heatmapRadius,
    setHeatmapRadius,
    heatmapSmoothness,
    setHeatmapSmoothness,
    heatmapOpacity,
    setHeatmapOpacity,
    gridSize,
    setGridSize,
    gridShowLabels,
    setGridShowLabels,
    gridOpacity,
    setGridOpacity,
    scatterShapes,
    setScatterShapes,
    trajectoryStyles,
    setTrajectoryStyles,
    teamColors,
    setTeamColors,
    statTypeColors,
    setStatTypeColors,
    selectedStatTypes,
    setSelectedStatTypes,
    selectedTags,
    setSelectedTags,
    
    // Computed values
    statTypeOptions,
    playerOptions,
    tagOptions,
    teamOptions,
    hasActiveFilters,
    hasFilteredData,
    shapesKey,
    stylesKey,
    
    // Helper functions
    handleFilterChange,
    handleClearFilters,
    getStatTypeColor,
    getVisualizationTypeForItem,
  };
};

